import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { SuiInputComponent } from '@sui/input';
import { Subscription } from 'rxjs';

import {
  IntervalUnit,
  RecurrenceDefinition,
  RecurrenceType,
} from '../../models/schedule.models';
import { NomenclatureService } from '../../services/nomenclature.service';

/**
 * Recurrence picker for background feed algorithms - the full scheduling-engine capability
 * set (interval / daily / weekly / monthly / cron), replacing the legacy interval-minutes
 * input.
 *
 * Registers three contract-shaped controls on the host form, so hosts that spread the raw form
 * value into the request payload keep working unchanged:
 * - `schedule` (boolean, the execute-now vs schedule mode)
 * - `schedule_recurrence` (a RecurrenceDefinition object, kept in sync from the internal picker)
 * - `schedule_timezone_id` (number | null)
 */
@Component({
  selector: 'app-algorithm-schedule',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    SuiInputComponent,
  ],
  templateUrl: './algorithm-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './algorithm-schedule.component.scss',
})
export class AlgorithmScheduleComponent implements OnInit, OnDestroy {
  @Input({ required: true }) parent!: FormGroup;
  @Input() initialSchedule = false;
  @Input() defaultIntervalMinutes = 60;
  @Input() editMode = false;
  @Input() initialRecurrence: RecurrenceDefinition | null = null;
  @Input() initialTimezoneId: number | null = null;

  private readonly nomService = inject(NomenclatureService);

  readonly weekdayOptions = [
    { id: 1, key: 'schedule-editor.weekday.mon' },
    { id: 2, key: 'schedule-editor.weekday.tue' },
    { id: 3, key: 'schedule-editor.weekday.wed' },
    { id: 4, key: 'schedule-editor.weekday.thu' },
    { id: 5, key: 'schedule-editor.weekday.fri' },
    { id: 6, key: 'schedule-editor.weekday.sat' },
    { id: 7, key: 'schedule-editor.weekday.sun' },
  ];

  readonly typeOptions: Array<{ value: RecurrenceType; labelKey: string }> = [
    { value: 'interval', labelKey: 'schedule-editor.button.interval' },
    { value: 'daily', labelKey: 'schedule-editor.button.daily' },
    { value: 'weekly', labelKey: 'schedule-editor.button.weekly' },
    { value: 'monthly', labelKey: 'schedule-editor.button.monthly' },
    { value: 'cron', labelKey: 'schedule-editor.button.cron' },
  ];

  readonly dayOfMonthOptions: Array<{ value: number; label: string }> = [
    ...Array.from({ length: 31 }, (_, index) => ({
      value: index + 1,
      label: `${index + 1}`,
    })),
    { value: 0, label: '' },
  ];

  timezones: Array<{ id: number; name: string }> = [];

  picker!: FormGroup;

  private pickerSubscription?: Subscription;

  ngOnInit(): void {
    const initial = this.initialRecurrence;

    this.picker = new FormGroup({
      type: new FormControl<RecurrenceType>(
        initial?.type &&
          initial.type !== 'windowed_interval' &&
          initial.type !== 'once'
          ? initial.type
          : 'interval',
      ),
      interval_value: new FormControl<number>(
        initial?.type === 'interval' && initial.interval_value
          ? this.toPickerInterval(initial).value
          : this.defaultIntervalMinutes,
        [Validators.min(1)],
      ),
      interval_unit: new FormControl<IntervalUnit>(
        initial?.type === 'interval' && initial.interval_value
          ? this.toPickerInterval(initial).unit
          : 'minutes',
      ),
      time_of_day: new FormControl<string>(initial?.time_of_day ?? '09:00'),
      weekdays: new FormControl<number[]>(initial?.weekdays ?? [1, 2, 3, 4, 5]),
      day_of_month: new FormControl<number>(initial?.day_of_month ?? 1),
      cron_expression: new FormControl<string>(initial?.cron_expression ?? ''),
      timezone_id: new FormControl<number | null>(
        this.initialTimezoneId != null ? Number(this.initialTimezoneId) : null,
      ),
    });

    if (!this.parent.contains('schedule')) {
      this.parent.addControl(
        'schedule',
        new FormControl(this.editMode ? true : this.initialSchedule),
      );
    }
    if (!this.parent.contains('schedule_recurrence')) {
      this.parent.addControl(
        'schedule_recurrence',
        new FormControl<RecurrenceDefinition | null>(null),
      );
    }
    if (!this.parent.contains('schedule_timezone_id')) {
      this.parent.addControl(
        'schedule_timezone_id',
        new FormControl<number | null>(this.initialTimezoneId),
      );
    }

    this.nomService.fetchTimezones().subscribe((timezones) => {
      // Number() the ids so the select's ngValue identity matches the numeric form control
      this.timezones = (timezones as Array<{ id: any; name: string }>).map(
        (timezone) => ({ id: Number(timezone.id), name: timezone.name }),
      );
    });

    this.pickerSubscription = this.picker.valueChanges.subscribe(() =>
      this.syncToParent(),
    );
    this.syncToParent();
  }

  ngOnDestroy(): void {
    this.pickerSubscription?.unsubscribe();
  }

  get pickerType(): RecurrenceType {
    return this.picker.get('type')?.value ?? 'interval';
  }

  get scheduleEnabled(): boolean {
    return this.editMode || !!this.parent.get('schedule')?.value;
  }

  isWeekdaySelected(id: number): boolean {
    return (this.picker.get('weekdays')?.value ?? []).includes(id);
  }

  toggleWeekday(id: number): void {
    const control = this.picker.get('weekdays');
    const current: number[] = control?.value ?? [];
    const next = current.includes(id)
      ? current.filter((day) => day !== id)
      : [...current, id].sort((a, b) => a - b);
    control?.setValue(next);
  }

  /**
   * Push the current picker state onto the parent's contract-shaped controls.
   */
  private syncToParent(): void {
    this.parent
      .get('schedule_recurrence')
      ?.setValue(this.toRecurrenceDefinition());
    this.parent
      .get('schedule_timezone_id')
      ?.setValue(this.picker.get('timezone_id')?.value ?? null);
  }

  /**
   * The engine RecurrenceDefinition for the current picker state - the backend validates it
   * and defaults interval types to completion anchoring.
   */
  private toRecurrenceDefinition(): RecurrenceDefinition {
    const value = this.picker.getRawValue();

    switch (value.type as RecurrenceType) {
      case 'daily':
        return { type: 'daily', time_of_day: value.time_of_day || '00:00' };
      case 'weekly':
        return {
          type: 'weekly',
          time_of_day: value.time_of_day || '00:00',
          weekdays: value.weekdays?.length ? value.weekdays : [1],
        };
      case 'monthly':
        return {
          type: 'monthly',
          time_of_day: value.time_of_day || '00:00',
          day_of_month: value.day_of_month ?? 1,
        };
      case 'cron':
        return {
          type: 'cron',
          cron_expression: (value.cron_expression ?? '').trim(),
        };
      default:
        return {
          type: 'interval',
          interval_value: Math.max(1, Number(value.interval_value) || 1),
          interval_unit: value.interval_unit ?? 'minutes',
        };
    }
  }

  /**
   * Normalize a stored interval recurrence into picker value + unit (whole hours collapse
   * from minutes for readability).
   */
  private toPickerInterval(definition: RecurrenceDefinition): {
    value: number;
    unit: IntervalUnit;
  } {
    const value = definition.interval_value ?? this.defaultIntervalMinutes;
    const unit = definition.interval_unit ?? 'minutes';

    if (unit === 'minutes' && value >= 60 && value % 60 === 0) {
      return { value: value / 60, unit: 'hours' };
    }

    return { value, unit };
  }
}
